import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material";
import { EMPTY_VALUE_PLACEHOLDER, formatTime } from "@shared";

import { CourtHearingResponse } from "@/api";
import { ClientLink } from "@/common/components/client-link/ClientLink";
import { useLanguageContext } from "@/common/contexts/language-context/LanguageContext";
import { locales } from "@/common/contexts/localization-date-context/LocalizationDateContextProvider.utils";
import { useTranslation } from "@/translations";

import { columnWidths, headerCellSx, notAssignedSx, tableSx } from "./CourtHearingsTable.styles";

interface Props {
  rows: CourtHearingResponse[];
  /** Id of the group heading the table belongs to, so a screen reader announces it with the table. */
  labelledById: string;
}

export function CourtHearingsTable({ rows, labelledById }: Props) {
  const { t } = useTranslation();
  const { language } = useLanguageContext();

  return (
    <TableContainer sx={{ overflowX: "auto" }}>
      <Table size="small" aria-labelledby={labelledById} sx={tableSx}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ ...headerCellSx, width: columnWidths[0] }}>{t("courtHearings.columns.time")}</TableCell>
            <TableCell sx={{ ...headerCellSx, width: columnWidths[1] }}>
              {t("courtHearings.columns.clientName")}
            </TableCell>
            <TableCell sx={{ ...headerCellSx, width: columnWidths[2] }}>
              {t("courtHearings.columns.caseNumber")}
            </TableCell>
            <TableCell sx={{ ...headerCellSx, width: columnWidths[3] }}>
              {t("courtHearings.columns.dutyCounselor")}
            </TableCell>
            <TableCell sx={{ ...headerCellSx, width: columnWidths[4] }}>
              {t("courtHearings.columns.fileNumber")}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.courseEntryId}>
              <TableCell sx={{ fontWeight: 700 }}>{formatTime(row.time, locales[language])}</TableCell>
              <TableCell>
                <ClientLink clientId={row.client.clientId} name={`${row.client.lastName}, ${row.client.firstName}`} />
              </TableCell>
              <TableCell>{row.client.caseNumber}</TableCell>
              <TableCell>
                {row.dutyCounselor ? (
                  (row.dutyCounselor.displayName ?? row.dutyCounselor.userCode)
                ) : (
                  <Typography component="span" variant="body2" sx={notAssignedSx}>
                    {t("courtHearings.report.notAssigned")}
                  </Typography>
                )}
              </TableCell>
              <TableCell>{row.fileNumber ?? EMPTY_VALUE_PLACEHOLDER}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
